/**
 * Single source of truth for documentation pages: order, labels, and navigation helpers.
 * Used by the sidebar expandable menu, docs layout (breadcrumbs, prev/next), and optional docs index.
 */

export const DOC_PAGES = [
  { href: "/docs/getting-started", label: "Getting Started" },
  { href: "/docs/adding-games", label: "Adding Games" },
  { href: "/docs/schedule-import", label: "Schedule Import" },
  { href: "/docs/payments", label: "Payment Tracking" },
  { href: "/docs/calendar", label: "Calendar & Scheduling" },
  { href: "/docs/locations", label: "Location Management" },
  { href: "/docs/partners", label: "Partner Management" },
  { href: "/docs/shortcuts", label: "Keyboard Shortcuts" },
  { href: "/docs/games-locations", label: "Adding Games & Locations" },
  { href: "/docs/partners-positions", label: "Adding Partners & Positions" },
  { href: "/docs/orgs-teams", label: "Adding Orgs & Teams" },
  { href: "/docs/manage-finances", label: "Manage Finances" },
] as const;

export type DocPageEntry = (typeof DOC_PAGES)[number];

/**
 * Training Zone panel uses a curated 2-column, 4-row layout.
 * Column 1 (rows 0,2,4,6), Column 2 (rows 1,3,5,7) — rendered in a 2-col grid.
 */
export const TRAINING_ZONE_PAGES = [
  { href: "/docs/getting-started", label: "Getting Started" },
  { href: "/docs/schedule-import", label: "Import Schedule" },
  { href: "/docs/games-locations", label: "Games & Locations" },
  { href: "/docs/payments", label: "Payment Tracking" },
  { href: "/docs/partners-positions", label: "Partners & Positions" },
  { href: "/docs/manage-finances", label: "Manage Finances" },
  { href: "/docs/orgs-teams", label: "Orgs & Teams" },
  { href: "/docs/calendar", label: "Calendar Sync" },
] as const;

export function getDocPageByPath(pathname: string): DocPageEntry | null {
  const normalized = pathname.replace(/\/$/, "") || "/";
  return DOC_PAGES.find((p) => p.href === normalized) ?? null;
}

export function getPrevNext(pathname: string): {
  prev: DocPageEntry | null;
  next: DocPageEntry | null;
} {
  const current = getDocPageByPath(pathname);
  if (!current) {
    return { prev: null, next: null };
  }
  const idx = DOC_PAGES.indexOf(current);
  return {
    prev: idx > 0 ? DOC_PAGES[idx - 1] ?? null : null,
    next: idx >= 0 && idx < DOC_PAGES.length - 1 ? DOC_PAGES[idx + 1] ?? null : null,
  };
}
